import { ref } from "vue";

interface LatLng {
  _lat: number;
  _lng: number;
}

interface LatLngBounds {
  _sw: LatLng; // 남서쪽 좌표
  _ne: LatLng; // 동북쪽 좌표
}

interface CheckAndUpdateWindow {
  checkAndUpdateWindow: () => boolean;
}

/**
 * 현재 중심 좌표 기준으로 반경 halfDistanceKm만큼(총 halfDistanceKm*2 폭) 사각형 윈도우 영역을 계산
 * @param center 현재 지도 중심 좌표
 * @param halfDistanceKm 중심에서 확장할 거리(km)
 * @returns 계산된 윈도우 바운더리
 */
const calculateWindowBounds = (
  center: LatLng,
  halfDistanceKm: number
): LatLngBounds => {
  // 1km당 위도 변화량 (약 1/111도)
  const latOffset = halfDistanceKm / 111;
  // 경도의 경우 현재 위도에 따라 변화량이 달라짐
  const lngOffset =
    halfDistanceKm / (111 * Math.cos((center._lat * Math.PI) / 180));

  return {
    _sw: {
      _lat: center._lat - latOffset,
      _lng: center._lng - lngOffset,
    },
    _ne: {
      _lat: center._lat + latOffset,
      _lng: center._lng + lngOffset,
    },
  };
};

/**
 * inner(내부) 바운더리가 outer(외부) 바운더리 안에 완전히 포함되는지 여부를 판단
 * @param inner 내부 바운더리
 * @param outer 외부 바운더리
 * @returns 포함되면 true, 아니면 false
 */
const isBoundsContained = (
  inner: LatLngBounds,
  outer: LatLngBounds
): boolean => {
  return (
    inner._sw._lat >= outer._sw._lat &&
    inner._sw._lng >= outer._sw._lng &&
    inner._ne._lat <= outer._ne._lat &&
    inner._ne._lng <= outer._ne._lng
  );
};

/**
 * 버스 정류장 마커 관리를 위한 윈도우 계산 컴포저블
 * - 현재 지도 바운더리가 기존 윈도우 범위를 벗어났는지 판단
 * - 벗어난 경우에만 윈도우를 갱신하고, 외부에 알리는 역할 수행
 *
 * @param map 지도 객체
 * @param windowSizeKm 윈도우 사이즈(Km 단위, 정사각형)
 */
export const useBusStopMakersWindow = (
  map: any,
  windowSizeKm: number
): CheckAndUpdateWindow => {
  const currentWindowBounds = ref<LatLngBounds | null>(null);

  /**
   * 지도 상의 현재 중심 좌표 기준으로 윈도우 영역을 갱신할지 말지 결정.
   * - 윈도우 범위를 벗어났다면 윈도우 갱신 후 true 반환
   * - 벗어나지 않았다면 아무것도 하지 않고 false 반환
   * @returns boolean (윈도우 갱신되면 true, 그대로면 false)
   */
  const checkAndUpdateWindow = (): boolean => {
    // 현재 지도 중심 좌표 가져오기
    const center = map.value.getCenter();
    const currentCenter: LatLng = { _lat: center.y, _lng: center.x };

    // 윈도우는 (windowSizeKm/2) 만큼 중심에서 확장
    const halfDistance = windowSizeKm / 2;
    const newWindowBounds = calculateWindowBounds(currentCenter, halfDistance);

    // 기존 윈도우가 존재한다면, 현재 지도에 보이는 바운더리가 그 내부에 모두 포함되는지 체크
    if (currentWindowBounds.value) {
      const mapBounds: LatLngBounds = map.value.getBounds();
      // mapBounds가 기존 윈도우 내부에 모두 포함되면 업데이트 불필요. false 반환
      if (isBoundsContained(mapBounds, currentWindowBounds.value)) {
        return false;
      }
    }

    // 윈도우 갱신
    currentWindowBounds.value = newWindowBounds;
    return true; // 윈도우가 새로 갱신되었으므로 true 반환
  };

  return {
    // currentWindowBounds,
    checkAndUpdateWindow,
  };
};
